import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { IDS } from '@/constants/testIds';

function StatusBadge({ status }) {
  const cls = `badge badge--${status || 'submitted'}`;
  return <span className={cls}>{status}</span>;
}

const ORDER_STATUS_LABELS = {
  draft: 'Draft', pending_approval: 'Pending your approval',
  changes_requested: 'Changes requested', approved: 'Approved',
  submitted: 'Submitted', fulfilled: 'Fulfilled',
  rejected: 'Rejected', cancelled: 'Cancelled',
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export default function Dashboard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/quotes/mine').then((r) => setQuotes(r.data)).catch(() => setQuotes([])),
      api.get('/orders/mine').then((r) => setOrders(r.data)).catch(() => setOrders([])),
    ]).finally(() => setLoading(false));
  }, []);

  const pending = orders.filter((o) => o.status === 'pending_approval');
  const otherOrders = orders.filter((o) => o.status !== 'pending_approval');

  return (
    <div className="container-x py-14" data-testid={IDS.dashboard.root}>
      <span className="eyebrow">Welcome</span>
      <h1 className="h-display text-3xl md:text-5xl mt-3">{user?.business_name || 'Your account'}.</h1>

      {pending.length > 0 && (
        <section className="mt-10 border border-[color:var(--ije-pink)] bg-[color:var(--ije-pink-tint)]/50 p-6" data-testid="dashboard-pending-orders">
          <p className="eyebrow" style={{ color: 'var(--ije-pink-dark)' }}>Awaiting your approval</p>
          <h2 className="serif text-2xl mt-2">{pending.length} order{pending.length === 1 ? '' : 's'} need{pending.length === 1 ? 's' : ''} your OK</h2>
          <div className="mt-4 divide-y divide-neutral-200/70">
            {pending.map((o) => (
              <Link key={o.id} to={`/orders/${o.id}`} className="grid grid-cols-[1fr_auto] py-3 items-center hover:opacity-70" data-testid={`dashboard-pending-${o.id}`}>
                <div>
                  <p className="serif text-lg">{o.order_number}</p>
                  <p className="sans text-xs text-neutral-500 tracking-widest uppercase mt-0.5">
                    {new Date(o.created_at).toLocaleDateString()} · {(o.items || []).length} items · {fmt(o.total)}
                  </p>
                </div>
                <span className="btn-primary" style={{ padding: '6px 14px' }}>Review</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="grid gap-10 lg:grid-cols-[1fr_320px] mt-14">
        <div>
          {otherOrders.length > 0 && (
            <>
              <h2 className="serif text-2xl mb-6">Order History</h2>
              <div className="border-t border-neutral-200 mb-14">
                {otherOrders.map((o) => (
                  <Link key={o.id} to={`/orders/${o.id}`} className="grid grid-cols-[1fr_auto_auto] gap-4 py-5 border-b border-neutral-100 items-center hover:bg-neutral-50 -mx-2 px-2" data-testid={`dashboard-order-${o.id}`}>
                    <div>
                      <p className="serif text-lg">{o.order_number}</p>
                      <p className="sans text-xs text-neutral-500 tracking-widest uppercase mt-1">
                        {new Date(o.created_at).toLocaleDateString()} · {(o.items || []).length} items
                      </p>
                    </div>
                    <span className={`badge badge--${o.status}`}>{ORDER_STATUS_LABELS[o.status] || o.status}</span>
                    <p className="serif text-lg text-right">{fmt(o.total)}</p>
                  </Link>
                ))}
              </div>
            </>
          )}

          <h2 className="serif text-2xl mb-6">Quote History</h2>
          {loading ? (
            <p className="sans text-xs tracking-widest uppercase text-neutral-500">Loading…</p>
          ) : quotes.length === 0 ? (
            <div className="border border-dashed border-neutral-200 p-12 text-center">
              <p className="serif text-xl italic">No quotes yet.</p>
              <Link to="/products" className="btn-outline mt-6 inline-flex">Browse Catalog</Link>
            </div>
          ) : (
            <div className="border-t border-neutral-200">
              {quotes.map((q) => (
                <div key={q.id} className="grid grid-cols-[1fr_auto_auto] gap-4 py-5 border-b border-neutral-100 items-center" data-testid={IDS.dashboard.quoteRow(q.id)}>
                  <div>
                    <p className="serif text-lg">{q.quote_number}</p>
                    <p className="sans text-xs text-neutral-500 tracking-widest uppercase mt-1">
                      {new Date(q.created_at).toLocaleDateString()} · {q.items.length} items
                    </p>
                  </div>
                  <StatusBadge status={q.status} />
                  <p className="serif text-lg text-right">${Number(q.total).toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="border border-neutral-200 p-6 h-fit" data-testid={IDS.dashboard.accountBlock}>
          <p className="eyebrow">Account Details</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div>
              <dt className="sans text-xs uppercase tracking-widest text-neutral-500">Business</dt>
              <dd className="sans mt-1">{user?.business_name}</dd>
            </div>
            <div>
              <dt className="sans text-xs uppercase tracking-widest text-neutral-500">Contact</dt>
              <dd className="sans mt-1">{user?.contact_name}</dd>
            </div>
            <div>
              <dt className="sans text-xs uppercase tracking-widest text-neutral-500">Email</dt>
              <dd className="sans mt-1">{user?.email}</dd>
            </div>
            <div>
              <dt className="sans text-xs uppercase tracking-widest text-neutral-500">Account Status</dt>
              <dd className="mt-1"><StatusBadge status={user?.status} /></dd>
            </div>
            <div>
              <dt className="sans text-xs uppercase tracking-widest text-neutral-500">Discount Tier</dt>
              <dd className="sans mt-1">{user?.discount_percent ? `${user.discount_percent}% off wholesale` : 'Standard (none)'}</dd>
            </div>
            {user?.shipping_address && (
              <div>
                <dt className="sans text-xs uppercase tracking-widest text-neutral-500">Shipping Address</dt>
                <dd className="sans mt-1 whitespace-pre-wrap">{user.shipping_address}</dd>
              </div>
            )}
          </dl>
        </aside>
      </div>
    </div>
  );
}
